// src/contexts/AuthStatusContext.tsx
import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import env from "../../config/env";

export type AuthStatus = 'loading' | 'unauth' | 'authorized';

interface AuthState {
    authStatus: AuthStatus;
    profile?: Record<string, unknown>;
    logout: () => Promise<void>;
}

const AuthStatusContext = createContext<AuthState | undefined>(undefined);

export const useAuthStatus = (): AuthState => {
    const ctx = useContext(AuthStatusContext);
    if (!ctx) throw new Error('useAuthStatus must be used inside <AuthStatusProvider>');
    return ctx;
};

export const AuthStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuth();              // from react-oidc-context
    const logout = async (): Promise<void> => {
        try {
            // Clear all local storage first
            localStorage.clear();
            sessionStorage.clear();

            // Clear all cookies for the current domain and Cognito domain
            const clearCookies = () => {
                // Get all cookies and clear them
                document.cookie.split(";").forEach((c) => {
                    const eqPos = c.indexOf("=");
                    const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
                    // Clear for current domain
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
                    // Clear for parent domain (in case of subdomains)
                    const parts = window.location.hostname.split('.');
                    if (parts.length > 1) {
                        const parentDomain = '.' + parts.slice(-2).join('.');
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${parentDomain}`;
                    }
                });
            };

            clearCookies();

            const clientId = env.COGNITO_CLIENT_ID;
            const cognitoDomain = env.COGNITO_DOMAIN;
            const signOutUri = `${window.location.origin}/signout`;

            // Force logout through Cognito's logout endpoint to clear Cognito session
            if (cognitoDomain && clientId) {
                // Use Cognito's logout endpoint directly to ensure session is cleared
                const logoutUrl = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(signOutUri)}&response_type=code`;

                // Clear everything again before redirect
                clearCookies();
                localStorage.clear();
                sessionStorage.clear();

                // Force redirect to Cognito logout
                window.location.href = logoutUrl;
                return;
            }

            // Fallback: try react-oidc-context signout
            if (auth.signoutRedirect && clientId) {
                await auth.signoutRedirect({
                    post_logout_redirect_uri: signOutUri,
                    extraQueryParams: {
                        client_id: clientId,
                        logout_uri: signOutUri
                    },
                });
                return;
            }
        } catch (err) {
            console.error("Logout failed", err);
        }

        // Final cleanup and redirect
        try {
            localStorage.clear();
            sessionStorage.clear();
            // Clear cookies one more time
            document.cookie.split(";").forEach((c) => {
                const eqPos = c.indexOf("=");
                const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            });
            window.location.href = '/signout';
        } catch (e) {
            console.error("Error during cleanup:", e);
            window.location.href = '/signout';
        }
    }
    const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
    const [profile, setProfile] = useState<Record<string, unknown>>();

    useEffect(() => {
        if (auth.isLoading) {
            setAuthStatus('loading');
            return;
        }
        if (!auth.isAuthenticated) {
            setAuthStatus('unauth');
            setProfile(undefined);
            return;
        }
        // authenticated
        setAuthStatus('authorized');
        setProfile(auth.user?.profile);
    }, [auth]);

    return (
        <AuthStatusContext.Provider value={{ authStatus, profile, logout }}>
            {children}
        </AuthStatusContext.Provider>
    );
};